import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import LangContext from "../../../../contexts/LangContext";
import { ASSET_HOST_BASE_URL, otherParty } from "../../../../config";
import { getIndependentSymbol, translate, translateNumber } from "../../../../helper";
import "./index.css";

const normalizeRegionKey = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? String(value || "") : parsed.toFixed(0);
};

const getRegionPathId = (regionId) => {
  const parsed = Number.parseFloat(regionId);
  return Number.isNaN(parsed) ? String(regionId || "") : parsed.toFixed(0);
};

const AreaCards = ({ tab, fetched, data, parties, searchParam }) => {
  const { lang } = useContext(LangContext);

  const partyMap = useMemo(() => {
    const map = new Map();
    parties.forEach((p) => map.set(p.code, p));
    return map;
  }, [parties]);

  const areas = useMemo(() => {
    if (!data || !Array.isArray(data.provinces)) return [];

    const regionFilter = normalizeRegionKey(searchParam?.region);
    const districtFilter = String(searchParam?.district || "");
    const provinceFilter = String(searchParam?.province || "");

    const flattened = [];
    data.provinces.forEach((province) => {
      province.districts.forEach((district) => {
        district.regions.forEach((region) => {
          if (region.rtype !== tab) return;

          const candidateList = Array.isArray(region.result) ? [...region.result] : [];
          const hasVotes = candidateList.some((c) => (c.vote || 0) > 0);
          if (hasVotes) {
            candidateList.sort((a, b) => (b.vote || 0) - (a.vote || 0));
          }

          flattened.push({
            provinceId: String(province.id),
            districtId: String(district.id),
            regionId: region.id,
            regionPathId: getRegionPathId(region.id),
            regionKey: normalizeRegionKey(region.id),
            districtName: district["name_" + lang] || district.name_en,
            regionName: region["name_" + lang] || region.name_en,
            totalCandidates: candidateList.length,
            candidates: candidateList.slice(0, 4),
          });
        });
      });
    });

    return flattened.filter((area) => {
      if (provinceFilter && area.provinceId !== provinceFilter) return false;
      if (districtFilter && area.districtId !== districtFilter) return false;
      if (regionFilter && area.regionKey !== regionFilter) return false;
      return true;
    });
  }, [data, lang, searchParam?.district, searchParam?.province, searchParam?.region, tab]);

  if (!fetched) {
    return (
      <section className="sh-area-results container">
        <div className="sh-area-results__header">
          <h3>{translate("select_region", lang)}</h3>
        </div>
        <div className="sh-area-results__grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sh-area-card-skeleton">
              <div className="sh-skeleton sh-skeleton-title" />
              <div className="sh-skeleton sh-skeleton-subtitle" />
              <div className="sh-skeleton-row">
                <div className="sh-skeleton sh-skeleton-avatar" />
                <div className="sh-skeleton sh-skeleton-line" />
              </div>
              <div className="sh-skeleton-row">
                <div className="sh-skeleton sh-skeleton-avatar" />
                <div className="sh-skeleton sh-skeleton-line" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="sh-area-results container">
      <div className="sh-area-results__header">
        {/* <h3>{translate("select_region", lang)}</h3> */}
        <h3>निर्वाचन क्षेत्रहरू </h3>
      </div>

      {areas.length === 0 ? (
        <div className="sh-area-results__empty">No results found</div>
      ) : (
        <div className="sh-area-results__grid">
          {areas.map((area, index) => (
            <article key={`${area.districtId}-${area.regionPathId}-${index}`} className="sh-area-card-lite">
              <header className="sh-area-card-lite__header">
                <h4>{area.districtName + " : " + area.regionName}</h4>
                <span>
                  {translate("candidate", lang)}: {translateNumber(area.totalCandidates, lang)}
                </span>
              </header>

              <div className="sh-area-card-lite__body">
                {area.candidates.map((candidate) => {
                  // console.log(candidate, 'candidate areacards');
                  const party =
                    partyMap.get(candidate.party) ||
                    (candidate.party?.match(/I+\d/) ? partyMap.get("I") : null) ||
                    otherParty;
                  let symbol = party.code.toUpperCase();
                  if (symbol === "I") {
                    symbol = getIndependentSymbol(area.districtId, area.regionPathId, candidate.name_en);
                  }
                  const gender = candidate.gender || "O";
                  const candidateImageUrl =
                    candidate.candidatephoto && candidate.candidatephoto.trim() !== ""
                      ? candidate.candidatephoto
                      : `https://result.election.gov.np/Images/Candidate/${candidate.candidateid}.jpg`;

                  return (
                    <div key={candidate.cid} className="sh-area-card-lite__candidate">
                      <img
                        src={candidateImageUrl}
                        alt={candidate["name_" + lang]}
                        className="sh-area-card-lite__avatar"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = ASSET_HOST_BASE_URL + "/images/candidates/dummy-" + gender + ".jpg";
                        }}
                      />
                      <div className="sh-area-card-lite__candidate-info">
                        <div className="sh-area-card-lite__candidate-name">{candidate["name_" + lang]}</div>
                        <div className="sh-area-card-lite__candidate-party">{party["short_name_" + lang] || party["name_" + lang]}</div>
                      </div>
                      <div class="sh-banner-winner-section">
                                    {candidate.elected  && <span className="sh-banner-winner">{ <img src={ "https://assets.deshsanchar.com/wp-content/uploads/2026/02/25194001/checklist2.png"} alt="winner" width="20px" height="20px" />}<span>{translate("elected", lang)}</span></span>}
                                    <span className="sh-winner data">
                                      <span>मत</span> {translateNumber(candidate.vote, lang)}
                                    </span>
                                  </div>
                      <img
                        src={ASSET_HOST_BASE_URL + "/images/parties/" + symbol + ".png"}
                        alt={party.code}
                        className="sh-area-card-lite__party-icon"
                        onError={({ currentTarget }) => {
                          currentTarget.onerror = null;
                          currentTarget.src = ASSET_HOST_BASE_URL + "/images/parties/O.png";
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <footer className="sh-area-card-lite__footer">
                <Link to={`/result/${area.provinceId}/${area.districtId}/${area.regionPathId}?type=${tab}&lang=${lang}`}>View details</Link>
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default AreaCards;
